import { getMarket } from '../modules/i18n';

// Holds back the topics of a tag navigation that do not reach the visitor's market, the way
// marketSpecificArticleCards.ts holds back cards, see docs/reference/articles/regionalised-tags.md
const NO_MARKET = 0;

type NavigationTagItem = { 'data-only-markets'?: string };

export function appliesToMarket(item: NavigationTagItem, market: number): boolean {
  const onlyMarkets = item['data-only-markets'];
  if (!onlyMarkets) {
    return true;
  }
  return onlyMarkets.split(',').some((marketUid) => parseInt(marketUid.trim(), 10) === market);
}

function readItems(navigation: Element): Array<NavigationTagItem> {
  try {
    const items = JSON.parse(navigation.getAttribute('items') ?? '[]');
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

function removeNavigation(navigation: Element): void {
  const parent = navigation.parentElement;
  if (parent && parent.tagName === 'JB-CONTENT-SPACING') {
    parent.remove();
    return;
  }
  navigation.remove();
}

/**
 * Runs before the component is defined, so its first render already leaves the regional topics
 * out, and returns every item for the second pass.
 */
export function hideRegionalItems(navigation: HTMLElement): Array<NavigationTagItem> {
  const items = readItems(navigation);
  const unrestricted = items.filter((item) => appliesToMarket(item, NO_MARKET));
  if (unrestricted.length !== items.length) {
    navigation.setAttribute('items', JSON.stringify(unrestricted));
  }
  if (unrestricted.length === 0) {
    navigation.style.setProperty('display', 'none');
  }
  return items;
}

/**
 * Puts back the topics of the visitor's market. The navigation is replaced rather than updated,
 * because the component sizes its "show all" toggle on its first render only.
 */
export function showItemsForMarket(navigation: HTMLElement, items: Array<NavigationTagItem>, market: number): void {
  const shown = items.filter((item) => appliesToMarket(item, market));
  if (shown.length === 0) {
    removeNavigation(navigation);
    return;
  }
  if (shown.length === readItems(navigation).length) {
    navigation.style.removeProperty('display');
    return;
  }
  const replacement = navigation.cloneNode(false) as HTMLElement;
  replacement.setAttribute('items', JSON.stringify(shown));
  replacement.style.removeProperty('display');
  navigation.replaceWith(replacement);
}

const navigations = [...document.querySelectorAll<HTMLElement>('jb-navigation-tags')]
  .map((navigation) => ({ navigation, items: hideRegionalItems(navigation) }))
  .filter(({ items }) => items.some((item) => !appliesToMarket(item, NO_MARKET)));

if (navigations.length > 0) {
  getMarket().then((market) => {
    const currentMarket = parseInt(market, 10) || NO_MARKET;
    navigations.forEach(({ navigation, items }) => showItemsForMarket(navigation, items, currentMarket));
  });
}
